import type {
  ObjectToolbarConfig,
  RegisterToolbarConfigArgs,
} from '@/blocks/object-toolbar';

/**
 * Assemble the object-toolbar config for the Issues register/list page
 * (CUS-156). Register toolbars are type-level: no `parentObject` (permissions
 * resolve against the type) and no `save` (a list page has no form). The
 * register's multi-format export (CSV / PDF / custom PDF) is surfaced as the
 * toolbar's "Download" group — one action per `ExportOption` returned by
 * `useExportOptions`, the same source that drives the legacy `ExportButton`.
 * The create button is surfaced as the toolbar's primary action, gated on
 * `insert:issue`.
 *
 * Takes the shared {@link RegisterToolbarConfigArgs} (see
 * `blocks/object-toolbar/types.ts`) — the input shape every module's register
 * builder uses across the CUS-136 fanout. Pure so it can be unit-tested without
 * rendering the page.
 */
export const buildIssueRegisterToolbarConfig = ({
  title,
  counter,
  exportOptions,
  create,
  editRibbon,
}: RegisterToolbarConfigArgs): ObjectToolbarConfig => ({
  header: { title, counter },
  actions: {
    ...(create
      ? {
          primary: [
            {
              id: 'create',
              label: create.label,
              iconName: 'plus',
              onClick: create.onClick,
              // No `canHaveAccessAsContributor` — the legacy
              // `<Permission permission={'insert:issue'}>` on the register page
              // does not pass it, and setting it would widen who sees Create.
              permission: 'insert:issue',
            },
          ],
        }
      : {}),
    ...(editRibbon
      ? {
          overflow: [
            {
              id: 'edit',
              label: editRibbon.groupLabel,
              actions: [
                {
                  id: 'edit-ribbon',
                  label: editRibbon.label,
                  iconName: 'filter-lines',
                  onClick: editRibbon.onClick,
                },
              ],
            },
          ],
        }
      : {}),
    export: [
      {
        id: 'export',
        actions: exportOptions.map((option) => ({
          id: option.id,
          label: option.text,
          iconName: 'download-01',
          onClick: option.exportFunction,
          disabled: option.disabled,
        })),
      },
    ],
  },
});
